/**
 * Tray Gateway — on-prem edge connectors (ADR-0042).
 *
 * Slice 0: the entity master list. A gateway is the cloud-side identity of an
 * on-prem agent that reaches firewalled databases / files. Register one here
 * (it starts `enrolling`), rename it, **revoke** it (the kill-switch — the
 * agent's cert is rejected on the next connect), or delete it. Enrollment +
 * the live heartbeat land in later slices.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  CheckCircle2,
  KeyRound,
  Pencil,
  Plus,
  RadioTower,
  RefreshCw,
  ShieldOff,
  Trash2,
  XCircle,
} from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EmptyState, PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import {
  gatewaysApi,
  type EnrollToken,
  type Gateway,
  type GatewayStatus,
} from "@/lib/api-gateways";
import { meApi } from "@/lib/api-me";
import { usePermissions } from "@/lib/permissions";

/** Map the gateway status to a badge tone. */
function statusTone(s: GatewayStatus): "neutral" | "active" | "warning" | "danger" {
  switch (s) {
    case "online":
      return "active";
    case "enrolling":
      return "warning";
    case "revoked":
      return "danger";
    default:
      return "neutral";
  }
}

type DialogState = { kind: "closed" } | { kind: "create" } | { kind: "edit"; gateway: Gateway };

export function GatewaysPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { can } = usePermissions();
  const canWrite = can("gateways", "write");
  const canDelete = can("gateways", "delete");

  const [dialog, setDialog] = React.useState<DialogState>({ kind: "closed" });
  const [enrollResult, setEnrollResult] = React.useState<EnrollToken | null>(null);
  const [detailId, setDetailId] = React.useState<string | null>(null);

  const listQ = useQuery({
    queryKey: ["gateways"],
    queryFn: () => gatewaysApi.list(),
  });
  const rows = listQ.data ?? [];

  // The cloud's current version == the latest agent version available (both are baked from
  // the same release tag). An online agent reporting an OLDER version → "update available".
  const meQ = useQuery({ queryKey: ["me"], queryFn: () => meApi.get(), staleTime: 5 * 60_000 });
  const cloudVersion = meQ.data?.app_version;
  const updateAvailable = (agentVersion: string | null): boolean =>
    !!agentVersion &&
    !!cloudVersion &&
    cloudVersion !== "dev" &&
    agentVersion !== "dev" &&
    agentVersion !== cloudVersion;

  const invalidate = () => qc.invalidateQueries({ queryKey: ["gateways"] });

  const revoke = useMutation({
    mutationFn: (id: string) => gatewaysApi.revoke(id),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => gatewaysApi.remove(id),
    onSuccess: invalidate,
  });
  const enroll = useMutation({
    mutationFn: (id: string) => gatewaysApi.enrollToken(id),
    onSuccess: (data) => {
      setEnrollResult(data);
      invalidate();
    },
  });

  return (
    <PageContainer>
      <PageHeader
        title={t("gateways.title", "Tray Gateway")}
        description={t(
          "gateways.subtitle",
          "Secure on-prem connectors that let Tray Cloud reach databases and files behind your firewall. Register a gateway, install its agent on-prem, and bind it to a connection.",
        )}
        actions={
          canWrite && (
            <Button size="sm" onClick={() => setDialog({ kind: "create" })}>
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              {t("gateways.new", "New gateway")}
            </Button>
          )
        }
      />
      <PageContent>
        {rows.length === 0 && !listQ.isLoading ? (
          <EmptyState
            icon={RadioTower}
            title={t("gateways.empty", "No gateways yet")}
            description={t(
              "gateways.emptyDescription",
              "Register a gateway to connect an on-prem network to Tray Cloud.",
            )}
          />
        ) : (
          <Table>
            <Thead>
              <Tr>
                <Th>{t("gateways.col.name", "Name")}</Th>
                <Th>{t("gateways.col.status", "Status")}</Th>
                <Th>{t("gateways.col.agent", "Agent")}</Th>
                <Th>{t("gateways.col.lastSeen", "Last seen")}</Th>
                <Th className="text-right">{t("common.actions", "Actions")}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((gw) => (
                <Tr key={gw.id}>
                  <Td>
                    <button
                      type="button"
                      className="text-left font-medium hover:underline"
                      onClick={() => setDetailId(gw.id)}
                    >
                      {gw.name}
                    </button>
                    {gw.description && (
                      <div className="text-xs text-muted-foreground">{gw.description}</div>
                    )}
                  </Td>
                  <Td>
                    <Badge tone={statusTone(gw.status)}>
                      {t(`gateways.status.${gw.status}`, gw.status)}
                    </Badge>
                  </Td>
                  <Td className="text-muted-foreground">
                    <span className="inline-flex items-center gap-2">
                      {gw.agent_version ?? "—"}
                      {updateAvailable(gw.agent_version) && (
                        <Badge tone="warning">
                          {t("gateways.updateAvailable", "aggiornamento disponibile")}
                        </Badge>
                      )}
                    </span>
                  </Td>
                  <Td className="text-muted-foreground">
                    {gw.last_seen_at ? new Date(gw.last_seen_at).toLocaleString() : "—"}
                  </Td>
                  <Td className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        title={t("gateways.detail.title", "Diagnostica")}
                        onClick={() => setDetailId(gw.id)}
                      >
                        <Activity className="h-4 w-4" strokeWidth={1.5} />
                      </Button>
                      {canWrite && (
                        <Button
                          size="icon"
                          variant="ghost"
                          title={t("common.edit", "Edit")}
                          onClick={() => setDialog({ kind: "edit", gateway: gw })}
                        >
                          <Pencil className="h-4 w-4" strokeWidth={1.5} />
                        </Button>
                      )}
                      {canWrite && gw.status !== "revoked" && (
                        <Button
                          size="icon"
                          variant="ghost"
                          title={t("gateways.enroll", "Enrollment token")}
                          disabled={enroll.isPending}
                          onClick={() => enroll.mutate(gw.id)}
                        >
                          <KeyRound className="h-4 w-4" strokeWidth={1.5} />
                        </Button>
                      )}
                      {canWrite && gw.status !== "revoked" && (
                        <Button
                          size="icon"
                          variant="ghost"
                          title={t("gateways.revoke", "Revoke")}
                          onClick={() => {
                            if (
                              confirm(
                                t(
                                  "gateways.confirmRevoke",
                                  `Revoke "${gw.name}"? Its agent will be locked out on the next connection.`,
                                ),
                              )
                            ) {
                              revoke.mutate(gw.id);
                            }
                          }}
                        >
                          <ShieldOff className="h-4 w-4 text-warning" strokeWidth={1.5} />
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          size="icon"
                          variant="ghost"
                          title={t("common.delete", "Delete")}
                          onClick={() => {
                            if (confirm(t("gateways.confirmDelete", `Delete "${gw.name}"?`))) {
                              remove.mutate(gw.id);
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-danger" strokeWidth={1.5} />
                        </Button>
                      )}
                    </div>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </PageContent>

      {dialog.kind !== "closed" && (
        <GatewayDialog
          gateway={dialog.kind === "edit" ? dialog.gateway : undefined}
          onClose={() => setDialog({ kind: "closed" })}
          onSaved={() => {
            invalidate();
            setDialog({ kind: "closed" });
          }}
        />
      )}

      {enrollResult && <EnrollDialog enroll={enrollResult} onClose={() => setEnrollResult(null)} />}

      {detailId && <GatewayDetailDialog id={detailId} onClose={() => setDetailId(null)} />}
    </PageContainer>
  );
}

/** Operator debugging drawer: identity + a LIVE agent-connectivity probe + health checks +
 *  the remote connections the agent holds. Refetches on open + manual refresh. */
function GatewayDetailDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ["gateways", "diagnostics", id],
    queryFn: () => gatewaysApi.diagnostics(id),
    refetchOnWindowFocus: false,
  });
  const d = q.data;

  const fmt = (v: string | null) => (v ? new Date(v).toLocaleString() : "—");
  const rowsMeta: Array<[string, React.ReactNode]> = d
    ? [
        [t("gateways.detail.status", "Stato"), t(`gateways.status.${d.status}`, d.status)],
        [t("gateways.detail.agentVersion", "Versione agente"), d.agent_version ?? "—"],
        [t("gateways.detail.lastSeen", "Ultimo contatto"), fmt(d.last_seen_at)],
        [t("gateways.detail.enrolledAt", "Iscritto il"), fmt(d.enrolled_at)],
        [t("gateways.detail.version", "Versione (revoche)"), String(d.version)],
        [
          t("gateways.detail.fingerprint", "Fingerprint cert"),
          d.cert_fingerprint_short ? (
            <code className="text-xs">{d.cert_fingerprint_short}</code>
          ) : (
            "—"
          ),
        ],
        [t("gateways.detail.createdBy", "Creato da"), d.created_by],
      ]
    : [];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {d?.name ?? t("gateways.detail.title", "Diagnostica")}
            {d && (
              <Badge tone={statusTone(d.status)}>
                {t(`gateways.status.${d.status}`, d.status)}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {q.isLoading && (
            <p className="text-sm text-muted-foreground">{t("common.loading", "Loading…")}</p>
          )}
          {d && (
            <>
              {d.enroll_pending && (
                <div className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning-foreground">
                  {t(
                    "gateways.detail.enrollPending",
                    "Token di iscrizione emesso ma non ancora usato dall'agente.",
                  )}
                </div>
              )}

              {/* health checks */}
              <div className="space-y-1.5">
                {d.checks.map((c) => (
                  <div key={c.key} className="flex items-center gap-2 text-sm">
                    {c.ok ? (
                      <CheckCircle2 className="h-4 w-4 text-success" strokeWidth={1.5} />
                    ) : (
                      <XCircle className="h-4 w-4 text-danger" strokeWidth={1.5} />
                    )}
                    <span className={c.ok ? "" : "text-muted-foreground"}>{c.label}</span>
                  </div>
                ))}
              </div>

              {/* metadata grid */}
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 border-t border-border/60 pt-3 text-sm">
                {rowsMeta.map(([k, v]) => (
                  <React.Fragment key={String(k)}>
                    <div className="text-muted-foreground">{k}</div>
                    <div className="text-right">{v}</div>
                  </React.Fragment>
                ))}
              </div>

              {/* remote connections behind the gateway */}
              <div className="border-t border-border/60 pt-3">
                <div className="mb-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t("gateways.detail.remotes", "Connessioni remote")}
                  {d.remotes_online ? "" : ` · ${t("gateways.detail.offline", "agente offline")}`}
                </div>
                {d.remotes.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t("gateways.detail.noRemotes", "Nessuna connessione segnalata dall'agente.")}
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {d.remotes.map((r) => (
                      <Badge key={r.ref} tone="neutral">
                        {r.ref}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" size="sm" disabled={q.isFetching} onClick={() => q.refetch()}>
            <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.5} />
            {t("common.refresh", "Aggiorna")}
          </Button>
          <Button size="sm" onClick={onClose}>
            {t("common.close", "Chiudi")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EnrollDialog({ enroll, onClose }: { enroll: EnrollToken; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("gateways.enrollTitle", "Enrollment token")}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <p className="text-sm text-warning">
            {t(
              "gateways.enrollOnce",
              "Shown ONCE. Paste it into the on-prem agent together with the CA below — it won't be retrievable again.",
            )}
          </p>
          <div className="space-y-1.5">
            <Label>{t("gateways.token", "Token")}</Label>
            <code className="block max-w-full overflow-x-auto rounded-md border bg-surface px-3 py-2 font-mono text-xs">
              {enroll.token}
            </code>
          </div>
          <div className="space-y-1.5">
            <Label>{t("gateways.caPem", "Gateway CA (pin this in the agent)")}</Label>
            <pre className="max-h-48 overflow-auto rounded-md border bg-surface px-3 py-2 font-mono text-xs">
              {enroll.ca_pem}
            </pre>
          </div>
          <p className="text-xs text-muted-foreground">
            {t("gateways.enrollExpires", "Expires")}: {new Date(enroll.expires_at).toLocaleString()}
          </p>
        </DialogBody>
        <DialogFooter>
          <Button onClick={onClose}>{t("common.done", "Done")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GatewayDialog({
  gateway,
  onClose,
  onSaved,
}: {
  gateway?: Gateway;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = React.useState(gateway?.name ?? "");
  const [description, setDescription] = React.useState(gateway?.description ?? "");

  const save = useMutation({
    mutationFn: () => {
      const body = { name: name.trim(), description: description.trim() || null };
      return gateway ? gatewaysApi.update(gateway.id, body) : gatewaysApi.create(body);
    },
    onSuccess: onSaved,
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {gateway
              ? t("gateways.editTitle", "Edit gateway")
              : t("gateways.newTitle", "New gateway")}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="gw-name">{t("gateways.col.name", "Name")}</Label>
            <Input
              id="gw-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("gateways.namePlaceholder", "e.g. HQ ERP network")}
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="gw-desc">{t("gateways.description", "Description")}</Label>
            <Input
              id="gw-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t("gateways.descPlaceholder", "Where this agent runs")}
            />
          </div>
          {save.isError && (
            <p className="text-sm text-danger">
              {t("gateways.saveError", "Could not save — the name may already be in use.")}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button disabled={!name.trim() || save.isPending} onClick={() => save.mutate()}>
            {t("common.save", "Save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
